import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel
} from '@mui/material';
import { ratingsService, Rating, RatingAnalytics } from '../services/ratingsService';
import { conversationsService, Conversation, Message } from '../services/conversationsService';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  FileDownload as FileDownloadIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Fingerprint as FingerprintIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { format, subDays, parseISO } from 'date-fns';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import ActionButtons, { ActionButton } from './ActionButtons';

// Types


interface RatingFormData {
  conversationId: string;
  messageId: string;
  rating: 'good' | 'bad' | 'wrong';
  notes: string;
}

// Conversation and Message interfaces imported from conversationsService

const COLORS = {
  good: '#4caf50',
  bad: '#ff9800',
  wrong: '#f44336'
};



const RatingsPageEnhanced: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [analytics, setAnalytics] = useState<RatingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState<'all' | 'good' | 'bad' | 'wrong'>('all');
  const [filterResolved, setFilterResolved] = useState<'unresolved' | 'resolved' | 'all'>('unresolved');
  
  // Analytics filters
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [customDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [showPercentages, setShowPercentages] = useState(false);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRating, setSelectedRating] = useState<Rating | null>(null);
  const [formData, setFormData] = useState<RatingFormData>({
    conversationId: '',
    messageId: '',
    rating: 'good',
    notes: ''
  });
  
  // Conversation details state
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Message[]>([]);
  const [conversationRatings, setConversationRatings] = useState<Rating[]>([]);
  const [conversationLoading, setConversationLoading] = useState(false);

  const loadAnalytics = useCallback(async () => {
    try {
      let startDate: Date | undefined;
      let endDate: Date | undefined;
      
      if (dateRange !== 'all') {
        startDate = dateRange === '7d' ? subDays(new Date(), 7) :
                   dateRange === '30d' ? subDays(new Date(), 30) :
                   dateRange === '90d' ? subDays(new Date(), 90) :
                   new Date(customDateRange.start);
        
        endDate = new Date();
      }

      const analytics = await ratingsService.getRatingAnalytics(startDate, endDate);
      setAnalytics(analytics);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  }, [dateRange, customDateRange]);

  const loadRatings = useCallback(async () => {
    try {
      setLoading(true);
      const { ratings } = await ratingsService.getAllRatings(50, 0);
      setRatings(ratings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRatings();
    loadAnalytics();
  }, [loadRatings, loadAnalytics]);

  useEffect(() => {
    if (dateRange !== 'all') {
      loadAnalytics();
    }
  }, [dateRange, customDateRange, loadAnalytics]);

  const handleCreateRating = async () => {
    try {
      setLoading(true);
      // TODO: Implement createRating in ratingsService  
      setError('Create rating functionality needs to be implemented with service account approach');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRating = async () => {
    if (!selectedRating) return;

    try {
      setLoading(true);
      // TODO: Implement updateRating in ratingsService
      setError('Update rating functionality needs to be implemented with service account approach');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRating = async () => {
    if (!selectedRating) return;

    try {
      setLoading(true);
      // TODO: Implement deleteRating in ratingsService
      setError('Delete rating functionality needs to be implemented with service account approach');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    if (!analytics) return;

    const data = {
      exportDate: new Date().toISOString(),
      dateRange: dateRange,
      totalRatings: analytics.totalRatings,
      distribution: analytics.distribution,
      ratingTrends: analytics.ratingTrends,
      recentRatings: analytics.recentRatings
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ratings-analytics-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openEditDialog = (rating: Rating) => {
    setSelectedRating(rating);
    setFormData({
      conversationId: rating.conversationId,
      messageId: rating.messageId || '',
      rating: rating.rating,
      notes: rating.notes || ''
    });
    setOpenDialog(true);
  };

  const openCreateDialog = () => {
    setSelectedRating(null);
    setFormData({
      conversationId: '',
      messageId: '',
      rating: 'good',
      notes: ''
    });
    setOpenDialog(true);
  };

  const getRatingIcon = (rating: 'good' | 'bad' | 'wrong') => {
    switch (rating) {
      case 'good':
        return <ThumbUpIcon />;
      case 'bad':
        return <ThumbDownIcon />;
      case 'wrong':
        return <WarningIcon />;
      default:
        return <ThumbUpIcon />;
    }
  };

  const getRatingColor = (rating: 'good' | 'bad' | 'wrong'): 'success' | 'warning' | 'error' | 'default' => {
    switch (rating) {
      case 'good':
        return 'success';
      case 'bad':
        return 'warning';
      case 'wrong':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredRatings = ratings.filter(rating => {
    const matchesSearch = searchTerm === '' || 
      rating.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rating.context?.userMessage.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rating.context?.aiResponse.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterRating === 'all' || rating.rating === filterRating;
    
        const matchesResolved = filterResolved === 'all' ||
      (filterResolved === 'resolved' && rating.resolved === true) ||
      (filterResolved === 'unresolved' && !rating.resolved);
    
    return matchesSearch && matchesFilter && matchesResolved;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewConversation = async (conversationId: string) => {
    try {
      setConversationLoading(true);
      setError(null);
      
      // Load messages and ratings (skip getConversationDetails to avoid CORS issue)
      const [messages, conversationRatings] = await Promise.all([
        conversationsService.getMessages(conversationId),
        ratingsService.getRatingsForConversation(conversationId)
      ]);
      
      // Create a minimal conversation object from the conversationId
      const conversationDetails: Conversation = {
        id: conversationId,
        userId: 'Unknown', // We'll get this from messages if available
        createdAt: new Date(),
        updatedAt: new Date(),
        messageCount: messages.length,
        status: 'active',
        sessionInfo: undefined,
        messagePreview: undefined
      };
      
      setSelectedConversation(conversationDetails);
      setConversationMessages(messages);
      setConversationRatings(conversationRatings);
      setConversationDialogOpen(true);
      
    } catch (error) {
      console.error('Error loading conversation:', error);
      setError(`Failed to load conversation details: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setConversationLoading(false);
    }
  };

  const handleCloseConversationDialog = () => {
    setConversationDialogOpen(false);
    setSelectedConversation(null);
    setConversationMessages([]);
    setConversationRatings([]);
  };

  const handleMarkResolved = async (ratingId: string, resolved: boolean = true) => {
    try {
      setError(null);
      setSuccessMessage(null);
      await ratingsService.markResolved(ratingId, resolved);
      
      // Update the rating in the local state
      setRatings(prevRatings => 
        prevRatings.map(rating => 
          rating.id === ratingId 
            ? { ...rating, resolved: resolved }
            : rating
        )
      );
      
      // Show success message with filter context
      const action = resolved ? 'resolved' : 'unresolved';
      console.log(`Rating ${ratingId} marked as ${action}`);
      
      // If showing unresolved only and marking as resolved, inform user
      if (resolved && filterResolved === 'unresolved') {
        setSuccessMessage(`✅ Rating marked as resolved! It has been hidden because you're viewing "Unresolved Only". Change filter to "All Ratings" to see resolved items.`);
      } else if (!resolved && filterResolved === 'resolved') {
        setSuccessMessage(`ℹ️ Rating marked as unresolved! It has been hidden because you're viewing "Resolved Only". Change filter to "All Ratings" to see unresolved items.`);
      } else {
        setSuccessMessage(`✅ Rating successfully marked as ${action}!`);
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      console.error('Error marking rating as resolved:', error);
      setError(`Failed to mark rating as ${resolved ? 'resolved' : 'unresolved'}`);
    }
  };

  const getMessageRating = (messageId: string) => {
    return conversationRatings.find(r => r.messageId === messageId);
  };

  const cleanupOrphanedRatings = async () => {
    try {
      setLoading(true);
      setError('Cleanup orphaned ratings functionality needs to be implemented with service account approach');
    } catch (error) {
      console.error('Error cleaning up orphaned ratings:', error);
      setError('Failed to cleanup orphaned ratings');
    } finally {
      setLoading(false);
    }
  };

  const paginatedRatings = filteredRatings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Prepare chart data
  const pieChartData = analytics && analytics.distribution ? [
    { name: 'Good', value: analytics.distribution.good, color: COLORS.good },
    { name: 'Bad', value: analytics.distribution.bad, color: COLORS.bad },
    { name: 'Wrong', value: analytics.distribution.wrong, color: COLORS.wrong }
  ].filter(item => item.value > 0) : [];

  const lineChartData = analytics?.ratingTrends.map(trend => ({
    date: format(parseISO(trend.date), 'MMM dd'),
    good: trend.good,
    bad: trend.bad,
    wrong: trend.wrong,
    total: trend.good + trend.bad + trend.wrong
  })) || [];

  if (loading && ratings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Conversation Ratings
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={cleanupOrphanedRatings}
            disabled={loading}
            size="small"
          >
            Cleanup Orphaned
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Rating
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      


      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
        <Tab icon={<AssessmentIcon />} label="Ratings List" />
        <Tab icon={<TrendingUpIcon />} label="Ratings Analytics" />
      </Tabs>

      {/* Ratings List Tab */}
      {activeTab === 0 && (
        <>
          {/* Search and Filter Controls */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search ratings..."
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                    endAdornment: searchTerm && (
                      <IconButton onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Rating</InputLabel>
                  <Select
                    value={filterRating}
                    label="Filter by Rating"
                    onChange={(e) => setFilterRating(e.target.value as any)}
                  >
                    <MenuItem value="all">All Ratings</MenuItem>
                    <MenuItem value="good">Good</MenuItem>
                    <MenuItem value="bad">Bad</MenuItem>
                    <MenuItem value="wrong">Wrong</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={filterResolved}
                    label="Filter by Status"
                    onChange={(e) => setFilterResolved(e.target.value as any)}
                  >
                    <MenuItem value="unresolved">Unresolved Only</MenuItem>
                    <MenuItem value="resolved">Resolved Only</MenuItem>
                    <MenuItem value="all">All Ratings</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                  {filteredRatings.length} rating{filteredRatings.length !== 1 ? 's' : ''} found
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Ratings Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Conversation</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRatings.map((rating) => (
                  <TableRow key={rating.id}>
                    <TableCell>
                      <Chip
                        icon={getRatingIcon(rating.rating)}
                        label={rating.rating.toUpperCase()}
                        color={getRatingColor(rating.rating) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rating.resolved ? 'Resolved' : 'Unresolved'}
                        color={rating.resolved ? 'success' : 'warning'}
                        size="small"
                        variant={rating.resolved ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        noWrap
                        sx={{ 
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleViewConversation(rating.conversationId)}
                      >
                        {rating.conversationId.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {rating.adminEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(rating.timestamp, 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={rating.notes || 'No notes'} placement="top">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 300, 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            lineHeight: '1.2em',
                            maxHeight: '2.4em'
                          }}
                        >
                          {rating.notes || 'No notes'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          onClick={() => {
                            setSelectedRating(rating);
                            setOpenViewDialog(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEditDialog(rating)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          onClick={() => {
                            setSelectedRating(rating);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      {rating.resolved ? (
                        <Tooltip title="Mark as Unresolved">
                          <IconButton 
                            onClick={() => handleMarkResolved(rating.id, false)}
                            color="warning"
                          >
                            <UndoIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Mark as Resolved">
                          <IconButton 
                            onClick={() => handleMarkResolved(rating.id, true)}
                            color="success"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredRatings.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      {/* Enhanced Analytics Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Analytics Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value as any)}
                  >
                    <MenuItem value="7d">Last 7 Days</MenuItem>
                    <MenuItem value="30d">Last 30 Days</MenuItem>
                    <MenuItem value="90d">Last 90 Days</MenuItem>
                    <MenuItem value="all">All Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showPercentages}
                      onChange={(e) => setShowPercentages(e.target.checked)}
                    />
                  }
                  label="Show Percentages"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportData}
                  disabled={!analytics}
                >
                  Export Data
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {analytics && (
            <Grid container spacing={3}>
              {/* Overview Cards */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Total Ratings</Typography>
                    <Typography variant="h4">{analytics.totalRatings}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Good Ratings</Typography>
                    <Typography variant="h4" color="success.main">
                      {analytics.distribution?.good || 0}
                    </Typography>
                    {showPercentages && analytics.totalRatings > 0 && analytics.distribution && (
                      <Typography variant="body2" color="text.secondary">
                        {Math.round((analytics.distribution.good / analytics.totalRatings) * 100)}%
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Bad Ratings</Typography>
                    <Typography variant="h4" color="warning.main">
                      {analytics.distribution?.bad || 0}
                    </Typography>
                    {showPercentages && analytics.totalRatings > 0 && analytics.distribution && (
                      <Typography variant="body2" color="text.secondary">
                        {Math.round((analytics.distribution.bad / analytics.totalRatings) * 100)}%
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Wrong Ratings</Typography>
                    <Typography variant="h4" color="error.main">
                      {analytics.distribution?.wrong || 0}
                    </Typography>
                    {showPercentages && analytics.totalRatings > 0 && analytics.distribution && (
                      <Typography variant="body2" color="text.secondary">
                        {Math.round((analytics.distribution.wrong / analytics.totalRatings) * 100)}%
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Charts */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Rating Distribution</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => 
                          `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Daily Rating Trends</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={lineChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="good" stroke={COLORS.good} name="Good" />
                      <Line type="monotone" dataKey="bad" stroke={COLORS.bad} name="Bad" />
                      <Line type="monotone" dataKey="wrong" stroke={COLORS.wrong} name="Wrong" />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Recent Ratings */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Recent Ratings</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Rating</TableCell>
                          <TableCell>Conversation</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.recentRatings.map((rating) => (
                          <TableRow key={rating.id}>
                            <TableCell>
                              <Chip
                                icon={getRatingIcon(rating.rating)}
                                label={rating.rating.toUpperCase()}
                                color={getRatingColor(rating.rating) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                noWrap
                                sx={{ 
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => handleViewConversation(rating.conversationId)}
                              >
                                {rating.conversationId.substring(0, 8)}...
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {format(rating.timestamp, 'MMM dd, HH:mm')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={rating.notes || 'No notes'} placement="top">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    maxWidth: 400, 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 3,
                                    lineHeight: '1.2em',
                                    maxHeight: '3.6em'
                                  }}
                                >
                                  {rating.notes || 'No notes'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedRating ? 'Edit Rating' : 'Add New Rating'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Conversation ID"
                  value={formData.conversationId}
                  onChange={(e) => setFormData({ ...formData, conversationId: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message ID (optional)"
                  value={formData.messageId}
                  onChange={(e) => setFormData({ ...formData, messageId: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Rating</InputLabel>
                  <Select
                    value={formData.rating}
                    label="Rating"
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value as any })}
                  >
                    <MenuItem value="good">Good</MenuItem>
                    <MenuItem value="bad">Bad</MenuItem>
                    <MenuItem value="wrong">Wrong</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={selectedRating ? handleUpdateRating : handleCreateRating}
            variant="contained"
            disabled={!formData.conversationId || !formData.rating}
          >
            {selectedRating ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Rating Details</DialogTitle>
        <DialogContent>
          {selectedRating && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">Rating Information</Typography>
                  <Typography><strong>Rating:</strong> {selectedRating.rating.toUpperCase()}</Typography>
                  <Typography><strong>Conversation ID:</strong> {selectedRating.conversationId}</Typography>
                  <Typography><strong>Admin:</strong> {selectedRating.adminEmail}</Typography>
                  <Typography><strong>Date:</strong> {format(selectedRating.timestamp, 'MMM dd, yyyy HH:mm:ss')}</Typography>
                  <Typography><strong>Notes:</strong> {selectedRating.notes || 'No notes'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">User Message</Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
                    <Typography>{selectedRating.context?.userMessage || 'No user message available'}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">AI Response</Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
                    <Typography>{selectedRating.context?.aiResponse || 'No AI response available'}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Rating</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this rating? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteRating} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversation Details Dialog */}
      <Dialog open={conversationDialogOpen} onClose={handleCloseConversationDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ChatIcon color="primary" />
              <Typography variant="h6">Conversation Details</Typography>
            </Box>
            
            {selectedConversation && (
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <FingerprintIcon fontSize="small" color="action" />
                  <Chip 
                    label={selectedConversation.id.substring(0, 12)}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Chip 
                    label={selectedConversation.userId.substring(0, 16)}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          
          {selectedConversation && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">
                  Created: {format(selectedConversation.createdAt, 'MMM dd, yyyy HH:mm')} • 
                  Messages: {selectedConversation.messageCount} • 
                  Device: {selectedConversation.sessionInfo?.deviceType || 'Unknown'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {conversationLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pb: 2 }}>
              <Paper sx={{ p: 2, height: '500px', overflowY: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  Messages
                </Typography>
                {conversationMessages.map((message) => (
                  <Box key={message.id} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="subtitle2" color={message.role === 'user' ? 'primary' : 'secondary'}>
                        {message.role === 'user' ? 'User' : 'Assistant'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(message.timestamp, 'MMM dd, yyyy HH:mm:ss')}
                      </Typography>
                      {getMessageRating(message.id) && (
                        <Chip
                          icon={getRatingIcon(getMessageRating(message.id)!.rating)}
                          label={`Rated: ${getMessageRating(message.id)!.rating}`}
                          color={getRatingColor(getMessageRating(message.id)!.rating) as any}
                          size="small"
                        />
                      )}
                    </Box>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: message.role === 'user' ? 'primary.light' : 'grey.100',
                        color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                      }}
                    >
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </Typography>

                      {/* RAG Sources (if available) */}
                      {message.metadata?.ragSources && message.metadata.ragSources.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            display: 'block',
                            mb: 0.5,
                            color: 'primary.main'
                          }}>
                            📚 Sources ({message.metadata.ragSources.length}):
                          </Typography>
                          {message.metadata.ragSources
                            .map((source: any, index: number) => ({ ...source, originalIndex: index }))
                            .sort((a: any, b: any) => (b.similarity || 0) - (a.similarity || 0))
                            .map((source: any, sortedIndex: number) => {
                              const isPrimary = sortedIndex === 0;
                              
                              const getTierLabel = (tier: number) => {
                                switch (tier) {
                                  case 1: return 'Critical';
                                  case 2: return 'Core';
                                  case 3: return 'Supporting';
                                  case 4: return 'Marketing';
                                  default: return 'Unknown';
                                }
                              };
                              
                              const getTierColor = (tier: number) => {
                                switch (tier) {
                                  case 1: return '#d32f2f'; // Red for Critical
                                  case 2: return '#ff9800'; // Orange for Core
                                  case 3: return '#1976d2'; // Blue for Supporting
                                  case 4: return '#388e3c'; // Green for Marketing
                                  default: return '#757575';
                                }
                              };

                              return (
                                <Box key={source.originalIndex} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ 
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    lineHeight: 1.4
                                  }}>
                                    <span style={{ 
                                      color: getTierColor(source.tier || 0),
                                      fontWeight: 600
                                    }}>
                                      {getTierLabel(source.tier || 0)}
                                    </span>
                                    {' • '}
                                    <span style={{ 
                                      fontWeight: isPrimary ? 600 : 500
                                    }}>
                                      {source.title || 'Unknown Source'}
                                    </span>
                                    {isPrimary && (
                                      <span style={{ 
                                        fontWeight: 600,
                                        color: '#2e7d32',
                                        marginLeft: 4
                                      }}>
                                        (primary)
                                      </span>
                                    )}
                                    {source.category && (
                                      <>
                                        {' • '}
                                        <span style={{ color: '#666' }}>
                                          {source.category}
                                        </span>
                                      </>
                                    )}
                                    {' • '}
                                    <span style={{ color: '#666' }}>
                                      {Math.round((source.similarity || 0) * 100)}% match
                                    </span>
                                  </Typography>
                                </Box>
                              );
                            })}
                        </Box>
                      )}

                      {/* Action Buttons (if available) */}
                      {message.actions && message.actions.length > 0 && (
                        <ActionButtons 
                          actions={message.actions.map((action: any, index) => ({
                            id: action.id || `action-${index}`,
                            type: action.type as 'book' | 'call' | 'email' | 'link' | 'quote',
                            label: action.label,
                            url: action.url,
                            phone: action.phone,
                            email: action.email,
                            style: action.style
                          }))} 
                          onButtonClick={(action: ActionButton) => {
                            console.log('Action button clicked in ratings page:', action);
                          }}
                        />
                      )}

                      {/* Rating Notes (if available) */}
                      {getMessageRating(message.id)?.notes && (
                        <Box sx={{ 
                          mt: 1.5, 
                          p: 1.5, 
                          bgcolor: 'grey.50', 
                          borderRadius: 1,
                          borderLeft: '4px solid',
                          borderLeftColor: 'grey.400'
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            display: 'block',
                            mb: 0.5,
                            color: 'text.primary'
                          }}>
                            📝 Rating Notes ({getMessageRating(message.id)!.rating}):
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}>
                            {getMessageRating(message.id)!.notes}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                ))}

                {/* Conversation-level rating notes */}
                {conversationRatings.filter(r => !r.messageId).map((conversationRating) => (
                  <Box key={conversationRating.id} sx={{ mt: 2 }}>
                    <Paper sx={{ 
                      p: 2, 
                      bgcolor: 'info.light',
                      borderLeft: '4px solid',
                      borderLeftColor: 'info.main'
                    }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark' }}>
                          💬 Conversation Rating
                        </Typography>
                        <Chip
                          icon={getRatingIcon(conversationRating.rating)}
                          label={conversationRating.rating}
                          color={getRatingColor(conversationRating.rating) as any}
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          by {conversationRating.adminEmail} • {format(conversationRating.timestamp, 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      </Box>
                      {conversationRating.notes && (
                        <Typography variant="body2" sx={{ 
                          color: 'info.dark',
                          fontStyle: 'italic',
                          mt: 1
                        }}>
                          "{conversationRating.notes}"
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConversationDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RatingsPageEnhanced; 